/**
 * Periodic discoverability hint for scheduled task deliveries (ENG-4457).
 *
 * Roughly every 10th scheduled delivery gets a follow-up message that
 * reminds the user their schedule is editable conversationally — Slack as
 * a thread reply, DM platforms as a second message. Uses a randomized
 * gate rather than a persisted counter because:
 *  - state survives nothing (no DB writes / per-task schedule counters)
 *  - sums to the right rate over time
 *  - no race between concurrent deliveries
 *
 * Behaviour is purely a function of `(random, probability, env)` so the
 * decision logic is unit-testable without spawning a delivery.
 */

const DEFAULT_PROBABILITY = 0.1;

/** Normalise an agent code_name to the env-var suffix form. Code names are
 *  kebab-case; env vars use UPPER_SNAKE. Non-ident chars become `_`. */
function envSuffixFor(codeName: string): string {
  return codeName.replace(/[^A-Za-z0-9]+/g, '_').toUpperCase();
}

/**
 * Configurable via env so operators can tune cadence or kill it entirely.
 *
 * Per-agent override takes precedence over host-global. For an agent with
 * code_name `phil-shout`:
 *   AGT_DELIVERY_HINT_DISABLED__PHIL_SHOUT=1    → 0 (disabled for this agent)
 *   AGT_DELIVERY_HINT_PROBABILITY__PHIL_SHOUT=0 → 0
 *   AGT_DELIVERY_HINT_PROBABILITY__PHIL_SHOUT=0.5 → 0.5
 *
 * Fallback chain: per-agent disable → per-agent probability → host disable
 * → host probability → default (0.1). This lets an operator dial down a
 * noisy agent without affecting the rest of the host.
 */
export function hintProbability(
  codeName?: string,
  env: NodeJS.ProcessEnv = process.env,
): number {
  const suffix = codeName ? `__${envSuffixFor(codeName)}` : '';

  // Per-agent disable wins outright — if an operator flagged a specific
  // agent off, a global probability override shouldn't re-enable it.
  if (suffix && env[`AGT_DELIVERY_HINT_DISABLED${suffix}`] === '1') return 0;

  const perAgent = suffix ? env[`AGT_DELIVERY_HINT_PROBABILITY${suffix}`] : undefined;
  if (perAgent != null) return clampProbability(perAgent);

  if (env['AGT_DELIVERY_HINT_DISABLED'] === '1') return 0;

  const host = env['AGT_DELIVERY_HINT_PROBABILITY'];
  if (host != null) return clampProbability(host);

  return DEFAULT_PROBABILITY;
}

function clampProbability(raw: string): number {
  const parsed = parseFloat(raw);
  if (!Number.isFinite(parsed)) return DEFAULT_PROBABILITY;
  if (parsed < 0) return 0;
  if (parsed > 1) return 1;
  return parsed;
}

/**
 * Pure decision gate. `rng` defaults to Math.random but is injectable so
 * tests can pin the outcome.
 */
export function shouldIncludeHint(
  probability: number,
  rng: () => number = Math.random,
): boolean {
  if (probability <= 0) return false;
  if (probability >= 1) return true;
  return rng() < probability;
}

/**
 * The hint text. 10 variants to avoid "I've seen this one before" fatigue
 * when a heavy user catches a couple in the same week. Each variant must
 * surface at least two concrete example prompts so the reader has
 * something specific to try.
 */
export const HINT_VARIANTS: ReadonlyArray<string> = Object.freeze([
  "Quick note: you can change this scheduled task just by asking me — e.g. \"Change the schedule for this task\" or \"Make this report less verbose in future\".",
  "By the way, this is on a schedule — if you'd like to tweak it, just say something like \"run this weekly instead of daily\" or \"make this report less verbose in future\" and I'll handle it.",
  "Heads up: I deliver this on a schedule you can edit conversationally. Try \"Change the schedule for this task\" or \"Make this report shorter\" whenever you want to adjust it.",
  "PS — no UI needed to tune this. Just tell me \"Send this at 9am instead\" or \"Skip weekends for this report\" and I'll update the schedule.",
  "FYI this is a scheduled delivery. You can reshape it in plain English — e.g. \"Make this fortnightly\" or \"Only include items from the last 24 hours\".",
  "You're the boss of this schedule — ask me things like \"Pause this for two weeks\" or \"Include a summary at the top next time\" and I'll apply it.",
  "Side note: you can say \"Change this to Mondays only\" or \"Drop the preamble in future reports\" and I'll update the task. No config screen required.",
  "Reminder: I run this on a schedule you can edit by talking. Good openers: \"Change when this fires\" or \"Make future reports more concise\".",
  "Small tip — this delivery is editable on the fly. Try \"Move this to afternoons\" or \"Cut the detail down in future runs\" and I'll retune it.",
  "If this cadence or format isn't quite right, just ask — \"Only run this on weekdays\" or \"Shorter summaries from now on\" both work, no form to fill out.",
]);

export function pickHintVariant(rng: () => number = Math.random): string {
  const idx = Math.floor(rng() * HINT_VARIANTS.length) % HINT_VARIANTS.length;
  return HINT_VARIANTS[idx]!;
}
